import InfoTooltip from '@components/InfoTooltip';
import { HeadCell, TextCell } from '@components/ui';
import { CircularProgress, IconButton, makeStyles, Popover, Tooltip, Typography } from '@material-ui/core';
import DataUsageIcon from '@material-ui/icons/DataUsage';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatMoney } from 'src/hooks/useCurrency';
import { usePurchaseAgentLimits } from 'src/hooks/usePurchaseAgents';
import { PurchaseAgentLimitUsage, PurchaseAgentListItem, PurchaseAgentState } from 'src/modules/generated/api';
import { PURCHASE_AGENT_DATE_TIME_FORMAT } from 'src/modules/purchase-agent';

const NO_VALUE = '–';

const useStyles = makeStyles((theme) => ({
  popover: {
    padding: theme.spacing(1.5, 2),
    minWidth: 240,
  },
  title: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  cycleStart: {
    display: 'block',
    marginTop: theme.spacing(1),
  },
  info: {
    display: 'block',
    maxWidth: 280,
    marginTop: theme.spacing(1),
  },
  headCell: {
    display: 'inline-flex',
    alignItems: 'center',
  },
}));

const useLimitUsageText = (usage?: PurchaseAgentLimitUsage) => {
  const { t } = useTranslation();
  const formatMoney = useFormatMoney();

  if (!usage) return undefined;

  const usedBudget = formatMoney(usage.usedBudget) ?? NO_VALUE;
  const maxBudget = formatMoney(usage.maxBudget);
  const remainingBudget = formatMoney(usage.remainingBudget);
  const usedVehicles = usage.usedVehicleCount ?? 0;

  return {
    budget: maxBudget ? `${usedBudget} / ${maxBudget}` : usedBudget,
    budgetRemaining: remainingBudget ? t('purchaseAgent.limits.remaining', { value: remainingBudget }) : undefined,
    vehicles: usage.maxVehicleCount != null ? `${usedVehicles} / ${usage.maxVehicleCount}` : String(usedVehicles),
  };
};

// A deactivated agent has no running cycle, its last usage would only be confusing.
const visibleUsage = (agent: PurchaseAgentListItem) =>
  agent.state === PurchaseAgentState.Deactivated ? undefined : agent.limitUsage;

export const PurchaseAgentLimitHeadCell = ({
  text,
}: {
  text: 'purchaseAgent.list.columns.budget' | 'purchaseAgent.list.columns.vehicles';
}): JSX.Element => {
  const { t } = useTranslation();
  const classes = useStyles();
  return (
    <span className={classes.headCell}>
      <HeadCell text={text} />
      <InfoTooltip xsIcon title={t('purchaseAgent.limits.info')} />
    </span>
  );
};

export const PurchaseAgentBudgetCell = ({ agent }: { agent: PurchaseAgentListItem }): JSX.Element => {
  const text = useLimitUsageText(visibleUsage(agent));
  return <TextCell text={text?.budget ?? NO_VALUE} supportText={text?.budgetRemaining} />;
};

export const PurchaseAgentVehiclesCell = ({ agent }: { agent: PurchaseAgentListItem }): JSX.Element => {
  const text = useLimitUsageText(visibleUsage(agent));
  return <TextCell text={text?.vehicles ?? NO_VALUE} />;
};

const LimitUsageSummary = ({ usage }: { usage?: PurchaseAgentLimitUsage }): JSX.Element => {
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const text = useLimitUsageText(usage);

  return (
    <>
      <div className={classes.row}>
        <Typography variant="body2">{t('purchaseAgent.list.columns.budget')}</Typography>
        <Typography variant="body2" align="right">
          {text?.budget ?? NO_VALUE}
          {text?.budgetRemaining ? (
            <Typography component="span" variant="caption" color="textSecondary" style={{ display: 'block' }}>
              {text.budgetRemaining}
            </Typography>
          ) : null}
        </Typography>
      </div>
      <div className={classes.row}>
        <Typography variant="body2">{t('purchaseAgent.list.columns.vehicles')}</Typography>
        <Typography variant="body2">{text?.vehicles ?? NO_VALUE}</Typography>
      </div>
      {usage?.cycleStart ? (
        <Typography variant="caption" color="textSecondary" className={classes.cycleStart}>
          {t('purchaseAgent.limits.cycleStart', {
            date: new Date(usage.cycleStart).toLocaleString(i18n.language, PURCHASE_AGENT_DATE_TIME_FORMAT),
          })}
        </Typography>
      ) : null}
      <Typography variant="caption" color="textSecondary" className={classes.info}>
        {t('purchaseAgent.limits.info')}
      </Typography>
    </>
  );
};

export const PurchaseAgentLimitsButton = ({ agent }: { agent: PurchaseAgentListItem }): JSX.Element | null => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = !!anchorEl;
  const { data, isLoading, isError } = usePurchaseAgentLimits(agent.id?.purchaseAgentId, { enabled: open });

  if (agent.state === PurchaseAgentState.Deactivated) return null;

  return (
    <>
      <Tooltip title={t('purchaseAgent.limits.button')} placement="top">
        <IconButton size="small" color="primary" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <DataUsageIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <div className={classes.popover}>
          <Typography variant="body2" className={classes.title}>
            {t('purchaseAgent.limits.title')}
          </Typography>
          {isLoading ? <CircularProgress size={20} /> : null}
          {isError ? (
            <Typography variant="body2" color="error">
              {t('purchaseAgent.drawer.detailLoadError')}
            </Typography>
          ) : null}
          {data ? <LimitUsageSummary usage={data} /> : null}
        </div>
      </Popover>
    </>
  );
};
